import { Calendar, ChevronRight, Clock, EllipsisVertical, MapPin, Plus, Video } from "lucide-react";
import { useState } from "react";
import { MockAvatarStack } from "@/src/common/components/app-mock/avatar";
import {
	getPeople,
	MEETING_FORMAT_LABEL,
	type MeetingStatus,
	MOCK_EVENTS_LIST,
	MOCK_MEETINGS_LIST,
	type MockCommunityMeeting,
} from "@/src/common/components/app-mock/mock-community";
import { MockButton, MockCard, MockChip, type MockChipColor, MockSelect, MockTabs } from "@/src/common/components/app-mock/primitives";
import { CalendarWidget } from "@/src/common/components/app-mock/web-screens/calendar-widget";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

const STATUS_CHIP: Record<MeetingStatus, { label: string; color: MockChipColor }> = {
	accepted: { label: "Accepted", color: "success" },
	declined: { label: "Declined", color: "danger" },
	pending: { label: "Pending", color: "accent" },
};

function FormatChip({ meeting }: { meeting: MockCommunityMeeting }) {
	return (
		<MockChip size="xs">
			{meeting.format === "in_person" ? <MapPin className="size-3" /> : <Video className="size-3" />}
			{meeting.format === "hybrid" ? <MapPin className="size-3" /> : null}
			{MEETING_FORMAT_LABEL[meeting.format]}
		</MockChip>
	);
}

function MeetingCard({ meeting }: { meeting: MockCommunityMeeting }) {
	const status = STATUS_CHIP[meeting.status];
	return (
		<MockCard dataMock="meeting-card">
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 space-y-1">
					<p className="truncate text-[11px] font-semibold text-neutral-900">{meeting.title}</p>
					<p className="flex items-center gap-1 text-[10px] text-neutral-500">
						<Clock className="size-3" />
						{meeting.time}
					</p>
					<div className="flex flex-wrap items-center gap-1">
						{meeting.live ? (
							<MockChip color="danger" size="xs">
								Live
							</MockChip>
						) : null}
						<FormatChip meeting={meeting} />
						<MockChip color={status.color} size="xs">
							{status.label}
						</MockChip>
						<MockAvatarStack people={getPeople(meeting.participantIds)} size={16} />
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-1">
					{meeting.status === "pending" ? (
						<>
							<MockButton size="xs">Accept</MockButton>
							<MockButton size="xs" variant="danger">
								Decline
							</MockButton>
						</>
					) : meeting.live ? (
						<MockButton size="xs" icon={Video}>
							Join
						</MockButton>
					) : (
						<MockButton size="xs" variant="ghost">
							View Details
						</MockButton>
					)}
					<EllipsisVertical className="size-3.5 text-neutral-500" aria-hidden="true" />
				</div>
			</div>
		</MockCard>
	);
}

export function TodaysSchedule({ includeEvents }: { includeEvents?: boolean }) {
	const { palette } = useCommunityVariant();
	const meetings = MOCK_MEETINGS_LIST.filter((meeting) => meeting.dayLabel === "Today" || meeting.live);
	const events = includeEvents ? MOCK_EVENTS_LIST.filter((event) => event.dateLabel === "Tomorrow").slice(0, 1) : [];
	return (
		<MockCard dataMock="todays-schedule">
			<span className="text-[11px] font-semibold text-neutral-900">Today's schedule</span>
			<ul className="mt-1.5 space-y-0.5">
				{meetings.map((meeting) => (
					<li key={meeting.id} className="flex items-center gap-2 rounded-lg p-1.5" data-mock="schedule-row">
						<div className="min-w-0 flex-1">
							{meeting.kicker ? (
								<p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: palette.accent }}>
									{meeting.kicker}
								</p>
							) : null}
							<p className="truncate text-[11px] font-bold text-neutral-900">{meeting.title}</p>
							<p className="flex items-center gap-1 text-[10px] text-neutral-500">
								{includeEvents ? <Video className="size-3" /> : <Clock className="size-3" />}
								{meeting.time}
							</p>
						</div>
						<ChevronRight className="size-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
					</li>
				))}
				{events.map((event) => (
					<li key={event.id} className="flex items-center gap-2 rounded-lg p-1.5" data-mock="schedule-row">
						<div className="min-w-0 flex-1">
							<p className="truncate text-[11px] font-bold text-neutral-900">{event.title}</p>
							<p className="flex items-center gap-1 text-[10px] text-neutral-500">
								<Calendar className="size-3" />
								{event.time}
							</p>
						</div>
						<ChevronRight className="size-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
					</li>
				))}
			</ul>
		</MockCard>
	);
}

interface WebMeetingsScreenProps {
	interactive?: boolean;
	sidebar?: boolean;
	maxMeetings?: number;
}

export function WebMeetingsScreen({ interactive, sidebar = true, maxMeetings }: WebMeetingsScreenProps) {
	const [tab, setTab] = useState(0);
	const [selectedDay, setSelectedDay] = useState<number | undefined>(undefined);
	const filtered = MOCK_MEETINGS_LIST.filter((meeting) =>
		tab === 0 ? meeting.kind === "one_on_one" || meeting.live : meeting.kind === "group",
	);
	const visible = maxMeetings ? filtered.slice(0, maxMeetings) : filtered;
	const sections: Array<{ label: string; meetings: MockCommunityMeeting[] }> = [];
	for (const meeting of visible) {
		const label = meeting.live ? "In progress" : meeting.dayLabel;
		const section = sections.find((entry) => entry.label === label);
		if (section) section.meetings.push(meeting);
		else sections.push({ label, meetings: [meeting] });
	}

	return (
		<div className="flex gap-4" data-mock="meetings">
			<div className="min-w-0 flex-1 space-y-3">
				<div className="flex items-center justify-between">
					<h2 className="text-sm font-semibold text-neutral-900">Meetings</h2>
					<MockButton size="xs" icon={Plus}>
						New meeting
					</MockButton>
				</div>
				<div className="flex items-center justify-between gap-2">
					<MockTabs items={["1:1", "Group"]} active={tab} onChange={interactive ? setTab : undefined} ariaLabel="Meeting type" />
					<MockSelect value="Upcoming" />
				</div>
				{sections.map((section) => (
					<div key={section.label} className="space-y-1.5">
						<p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">{section.label}</p>
						{section.meetings.map((meeting) => (
							<MeetingCard key={meeting.id} meeting={meeting} />
						))}
					</div>
				))}
			</div>
			{sidebar ? (
				<div className="hidden w-56 shrink-0 flex-col gap-3 lg:flex" data-mock="meetings-sidebar">
					<CalendarWidget selectedDay={selectedDay} onSelectDay={interactive ? setSelectedDay : undefined} />
					<TodaysSchedule />
				</div>
			) : null}
		</div>
	);
}
